/**
 * Meta Pixel (browser) — optional, per-seller via public API.
 * Fail-safe: never throws; no-ops if pixel not initialized.
 * Purchase uses eventID matching server CAPI (order id).
 */

let scriptPromise = null;
let initializedPixelId = null;
/** True after fbevents.js has finished loading (stub alone is not enough). */
let fbeventsLibraryReady = false;
/** Avoid duplicate PageView for the same URL in one pixel session (e.g. React Strict Mode). */
let lastPageViewPath = null;

function fbeventsScriptPresent() {
  if (typeof document === "undefined") return false;
  return Boolean(document.querySelector('script[src*="fbevents.js"]'));
}

/**
 * Meta requires this stub BEFORE fbevents.js runs; otherwise fbq is not defined (fbevents.js:20).
 * Same pattern as Meta's official base code snippet.
 */
function ensureFbqStub() {
  if (typeof window === "undefined") return;
  if (window.fbq) return;
  const n = (window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  });
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
}

/**
 * Load fbevents.js once; returns when the library is ready (not just the stub).
 */
export function loadMetaPixelScript() {
  if (typeof window === "undefined") {
    return Promise.resolve(false);
  }
  ensureFbqStub();

  if (fbeventsLibraryReady) {
    return Promise.resolve(true);
  }
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve) => {
    let settled = false;
    const finishOk = () => {
      if (settled) return;
      settled = true;
      fbeventsLibraryReady = true;
      resolve(true);
    };
    const finishFail = () => {
      if (settled) return;
      settled = true;
      scriptPromise = null;
      resolve(false);
    };

    const existing = document.querySelector('script[src*="fbevents.js"]');
    if (existing) {
      if (existing.getAttribute("data-prodinent-fbevents") === "error") {
        finishFail();
        return;
      }
      if (fbeventsLibraryReady) {
        finishOk();
        return;
      }
      const onLoad = () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onErr);
        finishOk();
      };
      const onErr = () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onErr);
        existing.setAttribute("data-prodinent-fbevents", "error");
        finishFail();
      };
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onErr);
      if (existing.src && existing.complete) {
        queueMicrotask(onLoad);
      }
      return;
    }

    const s = document.createElement("script");
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    s.setAttribute("data-prodinent-fbevents", "pending");
    s.onload = () => {
      s.setAttribute("data-prodinent-fbevents", "loaded");
      finishOk();
    };
    s.onerror = () => {
      s.setAttribute("data-prodinent-fbevents", "error");
      finishFail();
    };
    document.head.appendChild(s);
  });

  return scriptPromise;
}

/**
 * @param {string} pixelId
 * @returns {Promise<boolean>}
 */
export async function initMetaPixel(pixelId) {
  if (!pixelId || typeof window === "undefined") return false;
  const ok = await loadMetaPixelScript();
  if (!ok || typeof window.fbq !== "function") return false;

  if (initializedPixelId !== pixelId) {
    window.fbq("init", pixelId);
    initializedPixelId = pixelId;
    // Standard Meta base code: PageView right after init (first load).
    trackMetaPageView();
  }
  return true;
}

export function resetMetaPixelSession() {
  initializedPixelId = null;
  lastPageViewPath = null;
}

export function isMetaPixelInitialized() {
  return Boolean(initializedPixelId && typeof window !== "undefined" && typeof window.fbq === "function");
}

/**
 * SPA-safe PageView: skips if same pathname+search already sent for this pixel session.
 */
export function trackMetaPageView() {
  try {
    if (!isMetaPixelInitialized()) return;
    const path =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "";
    if (path && path === lastPageViewPath) return;
    if (path) lastPageViewPath = path;
    window.fbq("track", "PageView");
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {object} p
 * @param {string} p.orderID — must match CAPI event_id
 * @param {number} p.value
 * @param {string} [p.currency='INR']
 * @param {string[]} [p.contentIds]
 * @param {number} [p.numItems]
 */
export function trackMetaPixelPurchase({ orderID, value, currency, contentIds, numItems }) {
  try {
    if (!isMetaPixelInitialized() || !orderID) return;
    const payload = {
      value: Number(value) || 0,
      currency: currency || "INR",
      content_type: "product",
    };
    if (contentIds?.length) payload.content_ids = contentIds;
    if (numItems != null) payload.num_items = Number(numItems) || 0;
    window.fbq("track", "Purchase", payload, { eventID: String(orderID) });
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {object} p
 * @param {string[]} p.contentIds
 * @param {string} [p.contentName]
 * @param {number} [p.value]
 * @param {string} [p.currency='INR']
 */
export function trackMetaViewContent({ contentIds, contentName, value, currency }) {
  try {
    if (!isMetaPixelInitialized() || !contentIds?.length) return;
    const payload = {
      content_ids: contentIds,
      content_type: "product",
      currency: currency || "INR",
    };
    if (contentName) payload.content_name = contentName;
    if (value != null) payload.value = Number(value) || 0;
    window.fbq("track", "ViewContent", payload);
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {object} p
 * @param {string[]} p.contentIds
 * @param {string} [p.contentName]
 * @param {number} p.value — line total for this add (unit × qty)
 * @param {string} [p.currency='INR']
 * @param {number} [p.numItems]
 */
export function trackMetaAddToCart({ contentIds, contentName, value, currency, numItems }) {
  try {
    if (!isMetaPixelInitialized() || !contentIds?.length) return;
    const payload = {
      content_ids: contentIds,
      content_type: "product",
      value: Number(value) || 0,
      currency: currency || "INR",
    };
    if (contentName) payload.content_name = contentName;
    if (numItems != null) payload.num_items = Number(numItems) || 0;
    window.fbq("track", "AddToCart", payload);
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {object} p
 * @param {number} p.value — cart total for checkout
 * @param {string} [p.currency='INR']
 * @param {string[]} [p.contentIds]
 * @param {number} [p.numItems]
 */
export function trackMetaInitiateCheckout({ value, currency, contentIds, numItems }) {
  try {
    if (!isMetaPixelInitialized()) return;
    const payload = {
      value: Number(value) || 0,
      currency: currency || "INR",
      content_type: "product",
    };
    if (contentIds?.length) payload.content_ids = contentIds;
    if (numItems != null) payload.num_items = Number(numItems) || 0;
    window.fbq("track", "InitiateCheckout", payload);
  } catch (_) {
    /* ignore */
  }
}
